import { computed, inject, Injectable, Injector, resource } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { WorkflowService } from '@softapps/gewerbe/workflow';
import {
  MasterdataCostCenter,
  MasterdataGeneralLedger,
  MasterdataStockSymbol,
} from '@softapps/core/masterdata';
import { StockPostingRepository } from '../repository/stock-posting.repository';
import { Costcenter } from '../models/costcenter';
import { GeneralLedgerAccount } from '../models/general-ledger-account';
import { StockPosting } from '../models/stock-posting';
import { ItemPatch } from '../models/item';

export function toMasterdataCostCenter(c: Costcenter): MasterdataCostCenter {
  return {
    id: String(c.id),
    key: String(c.costCenter),
    name: c.accountDescription1 ?? null,
    name2: c.accountDescription2 ?? null,
    description: null,
    chartOfAccounts: null,
    responsible: null,
    lockedFrom: null,
    calculatedRiskFactor: null,
    budget: null,
    budgetUpdate: null,
    erpNumber: null,
    category: null,
    allowCostAccountingIndicators: null,
    type: null,
  };
}

export function fromMasterdataCostCenter(c: MasterdataCostCenter): Costcenter {
  return {
    id: Number(c.id),
    costCenter: Number(c.key),
    accountDescription1: c.name ?? '',
    accountDescription2: c.name2 ?? '',
  };
}

export function toMasterdataGeneralLedger(
  account: GeneralLedgerAccount
): MasterdataGeneralLedger {
  return {
    id: String(account.id),
    key: String(account.number),
    name: account.name ?? null,
    accountText1: null,
    accountText2: null,
    taxCode: null,
    lockedDate: null,
    deletionDate: null,
    vatId: null,
    budgetPosition: null,
    overheadRate: null,
    infoText: null,
    accountType: null,
    taxPostingType: null,
    expenseRevenueType: null,
    grossNet: null,
    recapitulativeStatement: null,
    debitCredit: null,
    planningType: null,
    costCategory: null,
    postingKind: null,
    costAccountingRelevant: false,
    costAccountingProposal: false,
    costObjectRequired: false,
    manualPostingAllowed: false,
    budgetAccounting: false,
    taxProposal: false,
    accountProposal: false,
    valueDatePosting: false,
    purchasingRelevant: false,
    externalService: false,
  };
}

function toMasterdataStockSymbol(stockPosting: StockPosting): MasterdataStockSymbol {
  return {
    id: String(stockPosting.symbolId),
    key: String(stockPosting.symbolId),
    name: stockPosting.symbol ?? null,
    stockEffect: '',
    costingRelevant: false,
    documentType: '',
    recordScope: '',
    active: true,
    palletHandling: '',
    wasteBalanceRelevant: false,
  };
}

@Injectable()
export class StockPostingResourceService {
  private readonly repository = inject(StockPostingRepository);
  private readonly injector = inject(Injector);
  private readonly workflowService = inject(WorkflowService);

  private readonly stockPostingResource = resource({
    params: () => this.workflowService.workflowId(),
    loader: async ({ params }) => {
      if (params === null) return null;
      try {
        const result = await this.repository.loadByWorkflowId(params);
        return result;
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return null;
      }
    },
  });

  readonly stockPosting = computed(() => this.stockPostingResource.value()?.stockPosting);
  readonly stockSymbol = computed(() => {
    const stockPosting = this.stockPosting();
    return stockPosting ? toMasterdataStockSymbol(stockPosting) : null;
  });
  readonly items = computed(() => {
    const items = this.stockPostingResource.value()?.items ?? [];
    return items;
  });
  readonly isLoading = this.stockPostingResource.isLoading;

  private readonly costCenterResource = resource({
    params: () => this.stockPosting()?.id,
    loader: async ({ params }): Promise<MasterdataCostCenter | null> => {
      if (params === undefined) return null;
      try {
        const costCenter = await this.repository.getCostCenter(params);
        return costCenter ? toMasterdataCostCenter(costCenter) : null;
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return null;
      }
    },
  });

  readonly costCenter = this.costCenterResource.value;

  async updateCostCenter(item: MasterdataCostCenter | null | undefined): Promise<void> {
    const stockPostingId = this.stockPosting()?.id;
    if (!stockPostingId || !item) return;

    const previousCostCenter = this.costCenterResource.value();
    const previousPosting = this.stockPostingResource.value();
    const costCenter = fromMasterdataCostCenter(item);

    this.costCenterResource.value.set(item);
    this.stockPostingResource.value.update((current) =>
      current
        ? {
            ...current,
            items: current.items.map((entry) => ({
              ...entry,
              item: { ...entry.item, costCenter },
            })),
          }
        : current
    );

    try {
      await this.repository.addCostCenter(stockPostingId, Number(item.key));
    } catch (error) {
      this.costCenterResource.value.set(previousCostCenter);
      this.stockPostingResource.value.set(previousPosting);
      handleRequestError(error, this.injector, { show: true });
    }
  }

  async updateSymbol(picked: MasterdataStockSymbol | null | undefined): Promise<void> {
    const stockPostingId = this.stockPosting()?.id;
    if (!stockPostingId || !picked) return;

    const updated = await this.repository.patchHeader(stockPostingId, {
      stockSymbolId: Number(picked.id),
    });

    this.stockPostingResource.value.update((current) =>
      current ? { ...current, stockPosting: updated } : current
    );
  }

  async updateItem(itemId: number, body: Partial<ItemPatch>): Promise<void> {
    const stockPostingId = this.stockPosting()?.id;
    if (!stockPostingId) return;

    const updatedItem = await this.repository.patchItem(stockPostingId, itemId, body);

    this.stockPostingResource.value.update((current) =>
      current
        ? {
            ...current,
            items: current.items.map((entry) =>
              entry.item.id === itemId ? { ...entry, item: updatedItem } : entry
            ),
          }
        : current
    );
  }
}
